import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';

const DEFAULT_GRADIENT =
  'linear-gradient(120deg, #ff6b00, #ffa14a, #ffd08a, #ff8f42, #ff6b00)';

const Frame = styled.div<{ $radius: number; $borderWidth: number }>`
  position: relative;
  height: 100%;
  border-radius: ${(props) => props.$radius}px;
  padding: ${(props) => props.$borderWidth}px;
  isolation: isolate;
`;

/* The gradient sits behind the content and is revealed only where the cursor
   mask paints, so the visible result reads as a lit segment of the border. */
const Glow = styled.div<{ $gradient: string }>`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  background: ${(props) => props.$gradient};
  opacity: 0;
  transition: opacity 300ms ease;
  pointer-events: none;
`;

const Hairline = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  border: 1px solid rgba(17, 17, 17, 0.08);
  pointer-events: none;
`;

const Surface = styled.div<{ $radius: number; $borderWidth: number }>`
  position: relative;
  z-index: 1;
  height: 100%;
  border-radius: ${(props) => Math.max(props.$radius - props.$borderWidth, 0)}px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
`;

interface ShineBorderProps {
  children: React.ReactNode;
  className?: string;
  /** Thickness of the lit border ring, in pixels. */
  borderWidth?: number;
  radius?: number;
  /** Diameter of the cursor spotlight that reveals the gradient. */
  spotlightSize?: number;
  gradient?: string;
}

/**
 * Wraps content in a rounded frame whose gradient border lights up under the
 * cursor. Mask position is written straight to the DOM so tracking the pointer
 * never re-renders the wrapped subtree.
 */
const ShineBorder: React.FC<ShineBorderProps> = ({
  children,
  className,
  borderWidth = 2,
  radius = 24,
  spotlightSize = 400,
  gradient = DEFAULT_GRADIENT,
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      const frame = frameRef.current;
      const glow = glowRef.current;
      if (!frame || !glow) return;

      const bounds = frame.getBoundingClientRect();
      const mask =
        `radial-gradient(${spotlightSize}px circle at ` +
        `${event.clientX - bounds.left}px ${event.clientY - bounds.top}px, ` +
        `#000 10%, transparent 60%)`;

      glow.style.setProperty('-webkit-mask-image', mask);
      glow.style.setProperty('mask-image', mask);
    },
    [spotlightSize]
  );

  const setGlow = useCallback((visible: boolean): void => {
    if (glowRef.current) glowRef.current.style.opacity = visible ? '1' : '0';
  }, []);

  return (
    <Frame
      ref={frameRef}
      className={className}
      $radius={radius}
      $borderWidth={borderWidth}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setGlow(true)}
      onMouseLeave={() => setGlow(false)}
    >
      <Glow ref={glowRef} $gradient={gradient} aria-hidden="true" />
      <Hairline aria-hidden="true" />
      <Surface $radius={radius} $borderWidth={borderWidth}>
        {children}
      </Surface>
    </Frame>
  );
};

export default ShineBorder;
